import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { SilaeFormData } from "@/types/silae";

// Une étape du « raisonnement » de l'agent, affichée dans la timeline de thinking.
// kind pilote l'icône et le libellé ; status pilote l'animation (en cours / fait).
export type ThinkingStepKind =
  | "thinking" // texte de raisonnement brut (thinking_delta)
  | "status" // statut système (l'agent interroge le modèle)
  | "question" // AskUserQuestion posée puis répondue
  | "delegate" // délégation à un sous-agent (Agent tool)
  | "tool" // appel d'outil (MCP : validation, recherche…)
  | "result"; // fin de run

export interface ThinkingStep {
  id: string;
  kind: ThinkingStepKind;
  /** Libellé court affiché sur la ligne (ex. « formdata_generator »). */
  label: string;
  /** Texte secondaire optionnel (ex. le contenu du thinking, en streaming). */
  detail?: string;
  status: "active" | "done";
  timestamp: number;
}

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  formData?: SilaeFormData;
  /** Étapes de raisonnement accumulées avant ce message assistant. */
  thinking?: ThinkingStep[];
  /** Durée totale du run (ms), renseignée à la réception du result. */
  thinkingDurationMs?: number;
}

export interface QuestionOption {
  label: string;
  description: string;
}

export interface Question {
  question: string;
  header: string;
  options: QuestionOption[];
  multiSelect: boolean;
}

export interface LimitReached {
  reason:
    | "error_max_budget_usd"
    | "error_max_turns"
    | "quota_exceeded"
    | "max_resumes"
    | "session_expired"
    | string;
  totalCostUsd?: number;
  numTurns?: number;
  percentUsed?: number;
  resetAt?: string;
  periodMonth?: string;
  // Set by the server: true when the conversation can be picked up on the same
  // session with its context intact. The UI never decides this itself.
  resumable?: boolean;
  sessionId?: string | null;
  resumesLeft?: number;
}

export interface QuotaStatus {
  percentUsed: number;
  resetAt: string;
  periodMonth: string;
}

interface AiChatState {
  messages: Message[];
  isStreaming: boolean;
  isWaitingForReply: boolean;
  sessionId: string | null;
  error: string | null;
  limitReached: LimitReached | null;
  quotaStatus: QuotaStatus | null;
  questions: Question[] | null;
  formData: SilaeFormData | null;
  generatedCode: string | null;
  suggestedName: string | null;
  script: string | null;
  // true dès que l'utilisateur a corrigé le code à la main via « Corriger le code ».
  // La sauvegarde doit alors stocker le code tel quel (bypass de la régénération
  // serveur depuis formData), sinon la correction est perdue en Mode A.
  codeManuallyEdited: boolean;
}

const initialState: AiChatState = {
  messages: [],
  isStreaming: false,
  isWaitingForReply: false,
  sessionId: null,
  error: null,
  limitReached: null,
  quotaStatus: null,
  questions: null,
  formData: null,
  generatedCode: null,
  suggestedName: null,
  script: null,
  codeManuallyEdited: false,
};

/**
 * Remplace le corps du premier bloc de code SILAE (fence ```silae ou ``` nu)
 * dans le markdown d'un message par `finalCode`. Les autres fences (json, etc.)
 * sont laissées intactes.
 */
function replaceSilaeCodeInContent(content: string, finalCode: string): string {
  let replaced = false;
  return content.replace(
    /```([\w-]*)\n([\s\S]*?)```/g,
    (match, lang: string) => {
      if (replaced) return match;
      const l = (lang || "").toLowerCase();
      if (l === "silae" || l === "") {
        replaced = true;
        return "```" + (lang || "silae") + "\n" + finalCode + "\n```";
      }
      return match;
    },
  );
}

const aiChatSlice = createSlice({
  name: "aiChat",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },

    upsertLastAssistantMessage: (state, action: PayloadAction<string>) => {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === "assistant") {
        last.content = action.payload;
      } else {
        state.messages.push({
          id: crypto.randomUUID(),
          role: "assistant",
          content: action.payload,
          timestamp: Date.now(),
        });
      }
    },

    // Garantit qu'un message assistant « vide » existe pour porter les étapes de
    // thinking dès le début du run (avant que le texte final ne soit streamé).
    // Renvoie implicitement le dernier message assistant via les helpers ci-dessous.

    // Ajoute (ou prolonge) une étape de raisonnement sur le message assistant courant.
    // Pour le thinking en streaming, on concatène les deltas sur la même étape tant
    // qu'elle est la dernière et de type "thinking".
    appendThinkingStep: (
      state,
      action: PayloadAction<{
        kind: ThinkingStep["kind"];
        label: string;
        detail?: string;
        /** true = mettre à jour la dernière étape de même kind au lieu d'en créer une. */
        coalesce?: boolean;
      }>,
    ) => {
      const { kind, label, detail, coalesce } = action.payload;

      // Trouver / créer le message assistant porteur.
      let target = state.messages[state.messages.length - 1];
      if (!target || target.role !== "assistant") {
        target = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "",
          timestamp: Date.now(),
          thinking: [],
        };
        state.messages.push(target);
      }
      if (!target.thinking) target.thinking = [];

      const steps = target.thinking;
      const lastStep = steps[steps.length - 1];

      if (coalesce && lastStep?.kind === kind && lastStep.status === "active") {
        // Concaténer le delta (thinking streaming) ou écraser le détail.
        lastStep.label = label || lastStep.label;
        lastStep.detail = (lastStep.detail ?? "") + (detail ?? "");
        return;
      }

      // Clore l'étape "thinking/status" active précédente quand une nouvelle commence.
      if (lastStep && lastStep.status === "active") {
        lastStep.status = "done";
      }

      steps.push({
        id: crypto.randomUUID(),
        kind,
        label,
        detail,
        status: "active",
        timestamp: Date.now(),
      });
    },

    // Marque une étape comme terminée (par kind + label, la plus récente active).
    completeThinkingStep: (
      state,
      action: PayloadAction<{ kind?: ThinkingStep["kind"]; label?: string }>,
    ) => {
      const { kind, label } = action.payload;
      const target = state.messages[state.messages.length - 1];
      if (!target?.thinking) return;
      for (let i = target.thinking.length - 1; i >= 0; i--) {
        const s = target.thinking[i];
        if (s.status !== "active") continue;
        if (kind && s.kind !== kind) continue;
        if (label && s.label !== label) continue;
        s.status = "done";
        return;
      }
    },

    // Clôt toutes les étapes encore actives et fige la durée totale du run.
    finalizeThinking: (state, action: PayloadAction<{ durationMs?: number }>) => {
      const target = [...state.messages].reverse().find((m) => m.role === "assistant");
      if (!target?.thinking) return;
      for (const s of target.thinking) {
        if (s.status === "active") s.status = "done";
      }
      if (action.payload.durationMs != null) {
        target.thinkingDurationMs = action.payload.durationMs;
      }
    },

    setStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
      if (!action.payload) {
        state.isWaitingForReply = false;
        state.questions = null;
      }
    },

    setWaitingForReply: (state, action: PayloadAction<boolean>) => {
      state.isWaitingForReply = action.payload;
    },

    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setLimitReached: (state, action: PayloadAction<LimitReached | null>) => {
      state.limitReached = action.payload;
    },

    setQuotaStatus: (state, action: PayloadAction<QuotaStatus | null>) => {
      state.quotaStatus = action.payload;
    },

    setQuestions: (state, action: PayloadAction<Question[] | null>) => {
      state.questions = action.payload;
    },

    setAiFormData: (
      state,
      action: PayloadAction<{
        formData: SilaeFormData;
        generatedCode: string | null;
        suggestedName: string | null;
      }>,
    ) => {
      state.formData = action.payload.formData;
      state.generatedCode = action.payload.generatedCode;
      state.suggestedName = action.payload.suggestedName;
      state.script = null;
      state.codeManuallyEdited = false;
      // Attach formData to the last assistant message so it persists across turns
      const lastAssistant = [...state.messages].reverse().find((m) => m.role === "assistant");
      if (lastAssistant) lastAssistant.formData = action.payload.formData;
    },

    setAiScript: (state, action: PayloadAction<string>) => {
      state.script = action.payload;
      state.formData = null;
      state.generatedCode = null;
      state.suggestedName = null;
      state.codeManuallyEdited = false;
    },

    // Adopte le code corrigé à la main : met à jour le bloc affiché (contenu du
    // dernier message assistant) ET le code servant à la sauvegarde en bibliothèque.
    applyCodeCorrection: (state, action: PayloadAction<string>) => {
      const finalCode = action.payload;
      state.codeManuallyEdited = true;
      // Mode B : le code vit dans `script`. Mode A : dans `generatedCode`.
      if (state.script !== null) state.script = finalCode;
      else state.generatedCode = finalCode;
      const lastAssistant = [...state.messages]
        .reverse()
        .find((m) => m.role === "assistant");
      if (lastAssistant) {
        lastAssistant.content = replaceSilaeCodeInContent(
          lastAssistant.content,
          finalCode,
        );
      }
    },

    // Dismiss the limit banner while keeping the conversation alive. This is the
    // difference between resuming and starting over: clearChat drops `messages`
    // and `sessionId`, which is exactly the context a resume needs to keep.
    clearLimitReached: (state) => {
      state.limitReached = null;
    },

    clearChat: (state) => {
      state.messages = [];
      state.sessionId = null;
      state.error = null;
      state.limitReached = null;
      // Note: quotaStatus is intentionally NOT reset — quota persists across conversations within the same month
      state.isStreaming = false;
      state.isWaitingForReply = false;
      state.questions = null;
      state.formData = null;
      state.generatedCode = null;
      state.suggestedName = null;
      state.script = null;
      state.codeManuallyEdited = false;
    },
  },
});

export const {
  addMessage,
  upsertLastAssistantMessage,
  appendThinkingStep,
  completeThinkingStep,
  finalizeThinking,
  setStreaming,
  setWaitingForReply,
  setSessionId,
  setError,
  setLimitReached,
  setQuotaStatus,
  setQuestions,
  setAiFormData,
  setAiScript,
  applyCodeCorrection,
  clearLimitReached,
  clearChat,
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
